import {
  defaultCultureForLanguage,
  formatCurrencyAmount,
  formatUtcAsLocal,
  parseUtcIso,
  toUtcIso,
} from './culture';
import { interpolate, JsonLocalizationService } from './JsonLocalizationService';
import { TenantTerminologyService } from './TenantTerminologyService';
import type {
  CultureCode,
  ILocalizationService,
  LanguageCode,
  LocalizationContext,
  StringParams,
  TenantId,
} from './types';

const STORAGE_CULTURE = 'luminary_culture';
const STORAGE_LANGUAGE = 'i18nextLng';
const STORAGE_TENANT = 'luminary_tenant';

/**
 * Microsoft.Extensions.Localization.IStringLocalizer<T> facade.
 * JSON base + tenant terminology override + culture formatters.
 */
export class LocalizationService implements ILocalizationService {
  private readonly json: JsonLocalizationService;
  private readonly tenantService: TenantTerminologyService;
  private contextState: LocalizationContext;

  constructor(
    json: JsonLocalizationService,
    tenantService: TenantTerminologyService,
    initial: LocalizationContext,
  ) {
    this.json = json;
    this.tenantService = tenantService;
    this.contextState = { ...initial };
  }

  get context(): LocalizationContext {
    return { ...this.contextState };
  }

  getString(key: string, params?: StringParams): string {
    const { language, tenantId } = this.contextState;
    const fallbackLang: LanguageCode = language === 'tr' ? 'en' : 'tr';

    const raw =
      this.json.getString(language, key) ??
      this.json.getString(fallbackLang, key) ??
      key;

    const withTenant = this.tenantService.resolve(tenantId, language, key, raw);
    return interpolate(withTenant, params);
  }

  setLanguage(language: LanguageCode): void {
    this.contextState.language = language;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_LANGUAGE, language);
    }
  }

  setCulture(culture: CultureCode): void {
    this.contextState.culture = culture;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_CULTURE, culture);
    }
  }

  async setTenant(tenantId: TenantId): Promise<void> {
    this.contextState.tenantId = tenantId;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_TENANT, tenantId);
    }
  }

  formatDate(
    value: Date | string | number,
    options: Intl.DateTimeFormatOptions = {
      dateStyle: 'medium',
      timeStyle: 'short',
    },
  ): string {
    const date =
      typeof value === 'string'
        ? parseUtcIso(value.endsWith('Z') ? value : `${value}Z`)
        : typeof value === 'number'
          ? new Date(value)
          : value;

    return formatUtcAsLocal(
      date,
      this.contextState.culture,
      this.contextState.timeZone,
      options,
    );
  }

  formatCurrency(amount: number, currency = this.contextState.currency): string {
    return formatCurrencyAmount(amount, this.contextState.culture, currency);
  }

  toUtcIso(value: Date): string {
    return toUtcIso(value);
  }

  parseUtc(isoUtc: string): Date {
    return parseUtcIso(isoUtc);
  }

  /** i18next resource bundle oluşturma */
  buildI18nResources(): Record<LanguageCode, Record<string, string>> {
    const languages: LanguageCode[] = ['tr', 'en'];
    const result = {} as Record<LanguageCode, Record<string, string>>;

    for (const lang of languages) {
      const base = { ...this.json.getDictionary(lang) };
      const tenantOverrides = this.tenantService.getOverrideDictionary(
        this.contextState.tenantId,
      );
      result[lang] = { ...base, ...tenantOverrides };
    }

    return result;
  }

  static readStoredCulture(): CultureCode | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_CULTURE) as CultureCode | null;
  }

  static readStoredLanguage(): LanguageCode | null {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_LANGUAGE);
    return raw === 'en' || raw === 'tr' ? raw : null;
  }

  static readStoredTenant(): TenantId | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_TENANT);
  }

  static defaultLanguage(): LanguageCode {
    return LocalizationService.readStoredLanguage() ?? 'tr';
  }
}

export function createLocalizationContext(partial?: Partial<LocalizationContext>): LocalizationContext {
  const language = partial?.language ?? LocalizationService.defaultLanguage();
  return {
    tenantId: partial?.tenantId ?? import.meta.env.VITE_TENANT_ID ?? 'luminary',
    language,
    culture:
      partial?.culture ??
      LocalizationService.readStoredCulture() ??
      defaultCultureForLanguage(language),
    timeZone: partial?.timeZone ?? resolveTimeZone(),
    currency: partial?.currency ?? (language === 'tr' ? 'TRY' : 'USD'),
  };
}

function resolveTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul';
  } catch {
    return 'Europe/Istanbul';
  }
}
